import React, {useEffect, useRef, useState} from 'react';
import {FormPageProps} from '@/components/WdiBus/FormPage';
import ProForm, {ProFormInstance} from '@ant-design/pro-form';
import {
  getRebatePaymentPageResult,
  initApplyPageContext,
  ViewEntityProps,
} from '@/pages/contract/RebatePaymentApply/components/data';
import {VatType} from '@/services/mdm/vattype';
import {WdiCardTitle, wdiCore, WdiInput, WdiModalAppResult} from '@/components/WdiControls';
import {Enum} from '@/enums/system';
import {ResponseStatus} from '@/utils/request';
import {Col, Form, Input, message, Row} from 'antd';
import {FileUploadUtility, WdiFileUpload} from '@/components/WdiControls/WdiFileUpload';
import {Invoices} from '@/services/sales/financial/invoices';
import DataStore from '@/components/GridWidget/DataStore';
import {PageType} from '@/services/basic/pagetype';
import {ApplyFooter, ApplyFooterType} from '@/components/WdiBus/ApplyFooter';
import {FormSp} from '@/services/sales/sp';
import {WdiPanel} from '@/components/WdiControls/WdiPanel';
import {WdiSelect, WdiSelectFormat} from '@/components/WdiControls/WdiSelect';
import {RebatePaymentDetailsForFgApplyPanel} from '@/pages/contract/RebatePaymentApply/components/detail';
import FormInvoiceForApplyPanel from '@/pages/sales/sp/components/FormInvoice';
import {BankAccount} from '@/services/mdm/bankaccount';
import {SoldTo} from '@/services/mdm/soldto';
import {WdiEditor} from "@/components/WdiControls/WdiEditor";
import SPWarningInfoApp from "@/pages/sales/sp/components/WarningInformation";
import {WarningInfoAppProps} from "@/components/WdiBus/PanelForWarning";


export const RebatePaymentArApplyApp: React.FC<FormPageProps> = (props) => {
  const formRef = useRef<ProFormInstance>();

  const [entity, setEntity] = useState<ViewEntityProps>();
  const [pageType, setPageType] = useState<API.PageType>();
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [invoiceDataSource, setInvoiceDataSource] = useState<API.FormInvoiceVo[]>([]);
  const [vatType, setVatType] = useState<API.VatType>();
  const [showBankNo, setShowBankNo] = useState(false);
  const [editorRef,setEditorRef] = useState<any>();
  const [warningSettings, setWarningSettings] = useState<WarningInfoAppProps>({visible: false});

  const onDetailsChange = (data: any) => {
    setDataSource(data);
  };

  const onInvoiceChange = (data: any) => {
    setInvoiceDataSource(data);
  };

  const onVatTypeChange = (vatTypeId?: number) => {
    formRef.current?.setFieldsValue({
      vatTypeId: vatTypeId
    });
    setVatType(undefined);
    if (vatTypeId) {
      VatType.get(vatTypeId).then(responseResult => {
        setVatType(responseResult.data);
        if (dataSource && dataSource.length > 0) {
          const newDataSource = [...dataSource];
          newDataSource.forEach(item => {
            item.vatTypeValue = responseResult.data.vatTypeValue;
            if (!wdiCore.Object.IsNullOrUndefined(item.amountInVat) && !wdiCore.Object.IsNullOrUndefined(item.vatTypeValue)) {
              item.amountExVat = wdiCore.Number.ToNumber(item.amountInVat / (1 + item.vatTypeValue), 2);
              item.tax = wdiCore.Number.ToNumber(item.amountInVat - item.amountExVat, 2);
            }
          })
          setDataSource(newDataSource);
        }
      })
    }
  }

  useEffect(() => {
    (async () => {
      const params = props.data;
      if (params) {
        const result = await initApplyPageContext(params);
        if (result.code !== ResponseStatus.SUCCESS) {
          message.error(result.message);
          return;
        }
        const viewEntity: ViewEntityProps = result.data;

        if (viewEntity.formSp) {
          formRef.current?.setFieldsValue({
            invoiceStatus: viewEntity.formSp.invoiceStatus,
            remark: viewEntity.formSp.remark,
            fileInfo: FileUploadUtility.parse(viewEntity.formSp.fileName, viewEntity.formSp.filePath)
          });
          Invoices.getForPayment(viewEntity.formSp.formSpId || 0).then((responseResult) => {
            let invoices = DataStore.init(responseResult.data);
            if (viewEntity.form?.relationType) {
              invoices = invoices.map(invoice => (Object.assign({}, invoice, {formInvoiceId: null})))
            }
            setInvoiceDataSource(invoices);
          });
          if (viewEntity.formSp.bankAccountId) {
            BankAccount.get(viewEntity.formSp.bankAccountId).then((res) => {
              formRef.current?.setFieldsValue({
                bankAccountId: { label: res.data.bankAccountNo, value: res.data.bankAccountId },
              });
            });
          }
          if (viewEntity.formSp && viewEntity.formSp.bankAccountName) {
            formRef.current?.setFieldsValue({
              bankAccountName: viewEntity.formSp.bankAccountName,
            });
          }
        }
        if (viewEntity.paymentMethod) {
          formRef.current?.setFieldsValue({
            paymentMethodName: viewEntity.paymentMethod.name,
          });
          if (viewEntity.paymentMethod.value == Enum.PaymentMethod.TRANSFER) {
            setShowBankNo(true);
          }
        }
        onVatTypeChange(viewEntity.vatTypeId);

        if (viewEntity.pageTypeId) {
          PageType.get(viewEntity.pageTypeId).then((response) => {
            setPageType(response.data);
          });
        }
        if (viewEntity.soldToCode) {
          SoldTo.get(viewEntity.soldToCode).then((res) => {
            formRef.current?.setFieldsValue({
              soldToCode: res.data.soldToCode,
              soldToNames: res.data.codeOrName,
            });
            if (!viewEntity.formSpDetails) {
              formRef.current?.setFieldsValue({
                soldToCodePayTo: { label: res.data.codeOrName, value: res.data.soldToCode },
              });
            }
          });
        }
        if (viewEntity.formSpDetails) {
          if (
            viewEntity.paymentMethodId == Enum.PaymentMethod.ACCOUNT_DEDUCT &&
            viewEntity.formSpDetails[0].soldToCodePayTo
          ) {
            SoldTo.get(viewEntity.formSpDetails[0].soldToCodePayTo).then((res) => {
              formRef.current?.setFieldsValue({
                soldToCodePayTo: { label: res.data.codeOrName, value: res.data.soldToCode },
              });
            });
          }
          setDataSource(DataStore.init(viewEntity.formSpDetails));
        }
        setEntity(viewEntity);
      }
    })();
  }, [props.data]);

  useEffect(() => {
    if (props.setPageTitle && pageType && entity) {
      let title = "返利核销申请";
      if (entity.formSp) {
        title += '【编辑】'
      }
      props.setPageTitle(title);
    }
  }, [props.setPageTitle, pageType, entity]);

  return (
    <>
      <SPWarningInfoApp {...warningSettings} />
      <ProForm
        formRef={formRef}
        initialValues={{ invoiceStatus: Enum.InvoiceStatus.WITH_INVOICE }}
        submitter={{
          render: () => {
            return (
              <ApplyFooter
                setLoading={props.setLoading}
                hiddenButton={[ApplyFooterType.Validate]}
                viewEntity={entity}
                submitBackUrl="/contract/myrebateinfo"
                doSubmit={async (type: number, forceSubmit: boolean) => {
                  if (!entity || !pageType) {
                    return;
                  }
                  const remark = await editorRef?.current?.getHtml?.();
                  const values = await formRef.current?.validateFieldsReturnFormatValue?.();
                  if (values && values.errorFields && values.errorFields.length) {
                    return;
                  }
                  const statusId =
                    type === ApplyFooterType.Draft
                      ? Enum.FormStatus.DRAFT
                      : Enum.FormStatus.AWAITING;
                  values.remark = remark;
                  const req = getRebatePaymentPageResult(
                    values,
                    entity,
                    pageType,
                    statusId,
                    dataSource,
                    invoiceDataSource,
                  );
                  if (req.code !== ResponseStatus.SUCCESS) {
                    message.error(req.message);
                    return;
                  }
                  props?.setLoading?.(true);
                  return await FormSp.Buttons.apply({
                    forceSubmit: forceSubmit,
                    ...req.data,
                  });
                }}
                del={async () => {
                  if (!entity || !entity.formId) {
                    return;
                  }
                  return await FormSp.Buttons.del(entity.formId);
                }}
                showWarning={(result: WdiModalAppResult, submitCallback: any) => {
                  setWarningSettings({
                    visible: true,
                    title: '警示信息',
                    wdiPanelList: result.data,
                    onClose: () => {
                      setWarningSettings({visible: false});
                    },
                    onSave: async () => {
                      return await submitCallback();
                    },
                  });
                }}
              />
            );
          },
        }}
      >
        <WdiPanel title={<WdiCardTitle title="返利核销信息" />}>
          <Row gutter={[16, 24]}>
            <Col className="gutter-row" span={4}>
              <Form.Item
                name="vatTypeId"
                label="可抵扣税率类型(普票为0)"
                rules={[{ required: true, message: '可抵扣税率类型不能为空' }]}
              >
                <WdiSelect
                  {...{
                    placeholder: '请选择可抵扣税率类型',
                    onChange: (value) => {
                      onVatTypeChange(value);
                    },
                    dataLoading: async (params) => {
                      const response = await VatType.search(
                        Object.assign({ isActive: true }, params),
                      );
                      return response.data.map((item) => {
                        return { value: item.vatTypeId, label: item.vatTypeName };
                      });
                    },
                  }}
                />
              </Form.Item>
            </Col>
            {!showBankNo && (
              <Col className="gutter-row" span={4}>
                <Form.Item
                  name="soldToCodePayTo"
                  label="付款客户"
                  rules={[{ required: true, message: '付款客户不能为空' }]}
                >
                  <WdiSelect
                    {...{
                      placeholder: '请选择付款客户',
                      showSearch: true,
                      remote: true,
                      labelInValue: true,
                      reloadKey: entity?.soldToCode,
                      dataLoading: async (params) => {
                        if (entity?.soldToCode) {
                          const soldToResp = await SoldTo.get(entity?.soldToCode);
                          const soldTo = soldToResp.data;
                          const response = await SoldTo.ajaxSearch(
                            Object.assign({
                              custSysCode: soldTo.custSysCode,
                              companyCode: soldTo.companyCode,
                            }, params),
                          );
                          const soldTos = [...response.data];
                          if (soldTos.findIndex(item => item.soldToCode == soldTo.soldToCode) < 0) {
                            soldTos.push(soldTo)
                          }
                          if (soldTos.length === 1) {
                            formRef.current?.setFieldsValue({
                              soldToCodePayTo: {value: soldTos[0].soldToCode, label: soldTos[0].codeOrName}
                            });
                          }
                          return soldTos?.map((item) => ({
                            value: item.soldToCode,
                            label: item.codeOrName,
                          }));
                        } else {
                          return [];
                        }
                      },
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            {showBankNo && (
              <Col className="gutter-row" span={4}>
                <Form.Item name="bankAccountId" label="银行账号" rules={[{ required: showBankNo }]}>
                  <WdiSelect
                    {...{
                      placeholder: '请选择银行账号',
                      reloadKey: entity?.soldToCode,
                      labelInValue: true,
                      dataLoading: async (params) => {
                        const response = await BankAccount.search(
                          Object.assign({ soldToCode: entity?.soldToCode, isActive: true }, params),
                        );
                        return response.data.map((item) => {
                          return {
                            value: item.bankAccountId,
                            label: item.bankAccountNo,
                          };
                        });
                      },
                      onChange: (value) => {
                        BankAccount.get(WdiSelectFormat.getvalue(value)).then((res) => {
                          formRef.current?.setFieldsValue({
                            bankAccountName: res.data.bankAccountName,
                          });
                        });
                      },
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            {showBankNo && (
              <Col className="gutter-row" span={4}>
                <Form.Item name="bankAccountName" label="户名" rules={[{ required: true }]}>
                  <WdiInput />
                </Form.Item>
              </Col>
            )}
            <Col className="gutter-row" span={4}>
              <Form.Item name="paymentMethodName" label="支付方式">
                <Input readOnly />
              </Form.Item>
            </Col>
            <Col hidden={true}>
              <Form.Item name="soldToCode" hidden={true} label="客户">
                <WdiInput readOnly />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item name="soldToNames" label="客户">
                <WdiInput readOnly />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 24]}>
            <Col className="gutter-row" span={16}>
              <Form.Item name="remark" label="核销备注">
                <WdiEditor setRef={setEditorRef} />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={8}>
              <Form.Item
                name="fileInfo"
                label="附件"
                valuePropName="fileList"
                getValueFromEvent={(e: any) => {
                  return e.fileList || [];
                }}
              >
                <WdiFileUpload leftTopInfo="请选择文件并上传(单个文件不能超500M)"/>
              </Form.Item>
            </Col>
          </Row>
        </WdiPanel>
        <RebatePaymentDetailsForFgApplyPanel
          data={{
            paymentMethodId: entity?.paymentMethodId,
            vatType: vatType,
            pageTypeId: entity?.pageTypeId,
            soldToCode: entity?.soldToCode,
          }}
          dataSource={dataSource}
          onChange={onDetailsChange}
        />
        <FormInvoiceForApplyPanel
          data={{
            vatType: vatType,
            details: dataSource,
            soldToCode: entity?.soldToCode,
            paymentMethodId: entity?.paymentMethodId,
          }}
          dataSource={invoiceDataSource}
          onChange={onInvoiceChange}
        />
      </ProForm>
    </>
  );
};
